import { Fragment, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { RotateCw } from "lucide-react";

import { Alert } from "../../../components/Alert/Alert";
import { Badge } from "../../../components/Badge/Badge";
import { Button } from "../../../components/Button/Button";
import { Checkbox } from "../../../components/Checkbox/Checkbox";
import { Input } from "../../../components/Input/Input";
import { Select } from "../../../components/Select/Select";
import type { MetaSchemaField } from "../../../components/MetaSchemaEditor/MetaSchemaEditor";
import type { SchemaField } from "../../../components/SchemaEditor/SchemaEditor";
import { FieldRow } from "../../projects/config/FieldRow";
import { profileSchemaLink, settingsLink } from "../links";
// The sample card prints its numbers the way the roster table prints the same
// numbers one tab over — grouped by a non-breaking space, from one function.
import { formatCount } from "../roster";
import {
  OUTSIDE_ALLOW_LIST,
  type BotProfileField,
  type BotProfileRowError,
  type BotsErrors,
} from "./botsValidation";
import type {
  BotGrowthRule,
  BotProfileMetaRule,
  BotProfileStatRule,
  BotProfileWeightedValue,
  BotsProfileConfig,
} from "../config/types";
import styles from "./BotProfileEditor.module.css";

interface Props {
  value: BotsProfileConfig;
  onChange: (next: BotsProfileConfig) => void;
  /** `profile.stats_schema` of this project — the Stats rows, in its order. */
  statsSchema: SchemaField[];
  /** `profile.meta_schema` of this project — the Meta rows, in its order. */
  metaSchema: MetaSchemaField[];
  /**
   * `profile.allowed_avatars` of this project — what the Avatar row may be
   * filled from. Empty is a project with no catalogue at all, where the field
   * is free-form for a bot exactly as it is for a living player.
   */
  allowedAvatars: string[];
  errors: BotsErrors;
  /** `bots.growth[]` — a stat listed there says how it grows and links to
   *  Progression, where it is edited. */
  growth: BotGrowthRule[];
  /** Whose schemas these are. This editor tells the operator four times to go
   *  and declare a key, and the Config card that declares it is on another
   *  page — a bare `#profile` anchor scrolled nowhere once this moved onto the
   *  Bots page. */
  projectID: string;
}

/** One line of the Stats group. */
interface StatRow {
  key: string;
  type: "int" | "bool" | "string";
}

/** One line of the Meta group. Every type `meta_schema` declares has a form:
 *  a pool for the string-ish two, and the stats collection's range / chance
 *  for the numeric two. */
interface MetaRow {
  key: string;
  type: "string" | "string[]" | "int" | "bool";
  allowed: string[];
}

/** Does this meta key draw from a weighted pool, or from a number? */
function drawsFromAPool(type: MetaRow["type"]): boolean {
  return type === "string" || type === "string[]";
}

const NOT_FILLED = "not filled — bots omit this key";

/** What the Avatar row says when the project restricts nothing. */
const FREE_FORM = "free-form — the key admits anything";

/**
 * An empty draw in a sample, wherever it lands.
 *
 * One spelling for every weighted pool, because a sample prints them one under
 * the other: t-133's mockup §5 shows `avatar — none —` and `badge — none —` in
 * the same three lines, and it is the same event both times — the pool's own
 * «— none —» chip won the draw, and this bot carries nothing for that key. The
 * card rows used to print a bare dash here, which read as a THIRD thing beside
 * the chip that caused it and the avatar line above it.
 */
const NONE_DRAWN = "— none —";

/** A value added by hand starts at a middling share, visible and easy to move.
 *  Weights are shares rather than percentages, so nothing has to add up. */
const NEW_VALUE_WEIGHT = 10;

/** The column the sample's values line up on, as the mockup prints them. */
const SAMPLE_KEY_WIDTH = 12;

/**
 * `bots.profile` — the rules a bot's avatar, stats and meta are rolled from.
 *
 * Mockup: .codelane/artifacts/t-82/bots-config-profile.html. The card rows are
 * NOT added by hand: they are the project's own `profile.stats_schema` and
 * `profile.meta_schema`, and nothing besides. That is the whole point of the
 * section — it says how to fill what the game already declared, and a bot can
 * never carry a key the game does not know. A project that wants its bots to
 * carry a score declares one among its stats like any other counter; there is
 * no key here the platform brings of its own.
 *
 * The Avatar row is the one exception, and it is not an exception to that rule
 * so much as a different catalogue: `players.avatar` is a column rather than a
 * card key, and what a project admits in it is `profile.allowed_avatars`. So
 * the row is drawn first — the order the game reads the card in — and it is
 * drawn even for a project that declares no schema at all.
 *
 * Types the roller has no shape for (`float` in stats, `int` / `bool` in meta)
 * get no row at all. A row that could be ticked but never filled in would be a
 * promise the platform does not keep.
 */
export function BotProfileEditor({
  value,
  onChange,
  statsSchema,
  metaSchema,
  allowedAvatars,
  errors,
  growth,
  projectID,
}: Props) {
  const schemaHref = profileSchemaLink(projectID);
  const progressionHref = settingsLink(projectID, "bots.growth");
  const statRows = useMemo(() => buildStatRows(statsSchema), [statsSchema]);
  const metaRows = useMemo(() => buildMetaRows(metaSchema), [metaSchema]);

  const avatarValues = value.avatar ?? [];
  // A value the catalogue no longer lists. An empty catalogue restricts
  // nothing, so nothing can fall outside it; «— none —» is never in a
  // catalogue and is never outside one either.
  const avatarOutside =
    allowedAvatars.length === 0
      ? []
      : avatarValues
          .filter((v) => v.value !== "" && !allowedAvatars.includes(v.value))
          .map((v) => v.value);

  /**
   * The Avatar row, and it is drawn WHEREVER this editor is — before the two
   * collections, in the order the game reads the card: the picture, then the
   * counters, then the cosmetics.
   *
   * It does not hang off the card schema at all: what a project admits here is
   * `profile.allowed_avatars`, so a game that declares neither stats nor
   * cosmetics still dresses its players and still has this one rule to fill.
   * There is no checkbox either — the rule is on exactly when it holds values,
   * and a tick beside an empty pool would be a second way to say the same
   * thing that could disagree with the first.
   */
  const avatarRow = (
    <FieldRow
      name="Avatar"
      apiName="bots.profile.avatar"
      stacked
      hint={
        <>
          The in-game asset a generated bot wears, drawn by weight from the
          project's <code>allowed_avatars</code>. <code>— none —</code> is a
          value like any other: it is how SOME bots are left without a picture
          rather than all of them, which is what a roster of living players
          looks like. An empty pool is no rule at all — bots are then created
          without an avatar, exactly as they are today. A value the whitelist no
          longer lists is dropped when the bot is created, the same way a card
          key outside the schema is.
        </>
      }
    >
      {/* Info rather than a warning: a project with no catalogue of avatars is
          not misconfigured, and an alarm over an ordinary shape teaches an
          operator to ignore alarms — the same reading as the missing-cosmetics
          notice below. */}
      {allowedAvatars.length === 0 && (
        <div className={styles.alertGap}>
          <Alert variant="info" title="This project lists no avatars">
            <code>profile.allowed_avatars</code> is empty, so nothing restricts
            what a player — or a bot — may wear. Values are typed by hand here
            and go to <code>players.avatar</code> as they stand.
          </Alert>
        </div>
      )}
      <div className={styles.editor}>
        <div
          className={`${rowClass(avatarValues.length > 0, errors.profileAvatar)} ${styles.plain}`}
        >
          <div className={styles.head}>
            <span className={styles.key}>players.avatar</span>
            <span className={styles.spacer} />
            <span className={styles.note}>
              {allowedAvatars.length > 0
                ? allowNote(allowedAvatars, avatarValues)
                : FREE_FORM}
            </span>
          </div>
          <div className={styles.roll}>
            <WeightedValues
              keyName="players.avatar"
              list={false}
              allowed={allowedAvatars}
              values={avatarValues}
              outside={avatarOutside}
              onChange={(avatar) => onChange({ ...value, avatar })}
            />
          </div>
          <RowErrors error={errors.profileAvatar} />
        </div>
      </div>
    </FieldRow>
  );

  // Neither catalogue declared: there is nothing for a bot to fill, and the
  // rows below would be a table of keys the platform invented for the game.
  // The Avatar row still stands — it answers to `profile.allowed_avatars`
  // rather than to a schema — so it is drawn first and the notice follows it.
  if (statsSchema.length === 0 && metaSchema.length === 0) {
    return (
      <>
        {avatarRow}
        <div className={styles.alertGap}>
          <Alert
            variant="warn"
            title="This project declares neither stats_schema nor meta_schema"
          >
            There is nothing for a bot to fill: both catalogues are empty, so
            the platform would be inventing keys the game has never heard of.
            Declare them in{" "}
            <Link to={schemaHref}>Profile → Stats schema / Meta schema</Link>,
            then come back — the rows here are that schema, one card per key.
          </Alert>
        </div>
        <div className={styles.previewBox}>
          <code>bots.profile.stats</code> and <code>bots.profile.meta</code>{" "}
          stay absent from the config: there is no card to fill. A bot is
          created with a name and a country — and an avatar only if the rule
          above draws one for it.
        </div>
      </>
    );
  }

  const statOrder = statRows.map((r) => r.key);
  const metaOrder = metaRows.map((r) => r.key);
  const statAt = indexByKey(value.stats);
  const metaAt = indexByKey(value.meta);

  const putStat = (key: string, rule: BotProfileStatRule | null) =>
    onChange({
      ...value,
      stats: writeRule(value.stats, statOrder, key, rule),
    });
  const putMeta = (key: string, rule: BotProfileMetaRule | null) =>
    onChange({
      ...value,
      meta: writeRule(value.meta, metaOrder, key, rule),
    });

  return (
    <>
      {avatarRow}

      <div className={styles.alertGap}>
        <Alert
          variant="info"
          title="A bot is an ordinary player, and a player has stats"
        >
          The guild roster hands the client every member's <code>meta</code> and{" "}
          <code>stats</code>. A bot with none of them is a row of zeros between
          real players — the cheapest tell there is. The rows below are your
          project's own schema (
          <Link to={schemaHref}>profile.stats_schema</Link> /{" "}
          <Link to={schemaHref}>profile.meta_schema</Link>): a bot can never
          carry a key the game does not know.
        </Alert>
      </div>

      <FieldRow
        name="Stats"
        apiName="bots.profile.stats"
        stacked
        hint={
          <>
            Rolled once, when the bot is created. An unticked key is not written
            at all — not <code>0</code>, not <code>null</code> — so the game
            reads it exactly as it reads a player who has never earned it.
          </>
        }
      >
        <div className={styles.editor}>
          {statRows.map((row) => {
            const at = statAt.get(row.key);
            const rule = at === undefined ? undefined : value.stats[at];
            const error = at === undefined ? undefined : errors.profileStats[at];
            const patch = (next: BotProfileStatRule) => putStat(row.key, next);
            // The mockup draws the wide box on a six-digit start, whatever
            // the key is called (L308-L322).
            const box = (group: BotProfileField) =>
              numBox(error, group, group === "start" && holdsABigNumber(rule));
            const grows = growth.find((g) => g.key === row.key);
            return (
              <div key={row.key} className={rowClass(!!rule, error)}>
                <div className={styles.head}>
                  <Checkbox
                    checked={!!rule}
                    aria-label={`Rule for ${row.key}`}
                    onChange={(on) =>
                      putStat(row.key, on ? blankRule(row.key, row.type) : null)
                    }
                  />
                  <span className={styles.key}>{row.key}</span>
                  <Badge>{row.type}</Badge>
                  <span className={styles.spacer} />
                  {!rule && <span className={styles.note}>{NOT_FILLED}</span>}
                </div>

                {rule && row.type === "int" && (
                  <div className={styles.roll}>
                    <span className={styles.micro}>start</span>
                    <Input
                      {...box("start")}
                      type="number"
                      value={rule.start_min ?? ""}
                      aria-label={`${row.key} start minimum`}
                      onChange={(e) =>
                        patch(withNumber(rule, "start_min", e.target.value))
                      }
                    />
                    <span className={styles.dash}>–</span>
                    <Input
                      {...box("start")}
                      type="number"
                      value={rule.start_max ?? ""}
                      aria-label={`${row.key} start maximum`}
                      onChange={(e) =>
                        patch(withNumber(rule, "start_max", e.target.value))
                      }
                    />
                    {grows && (
                      <>
                        <span className={styles.vsep} />
                        <span className={styles.moved}>
                          {grows.mode === "per_day"
                            ? "grows per day"
                            : "grows during play"}{" "}
                          · <Link to={progressionHref}>Progression →</Link>
                        </span>
                      </>
                    )}
                  </div>
                )}

                {rule && row.type === "bool" && (
                  <div className={styles.roll}>
                    <span className={styles.micro}>true in</span>
                    <Input
                      {...box("true_percent")}
                      type="number"
                      value={rule.true_percent ?? ""}
                      aria-label={`${row.key} true percent`}
                      onChange={(e) =>
                        patch(withNumber(rule, "true_percent", e.target.value))
                      }
                    />
                    <span className={styles.unit}>% of bots</span>
                  </div>
                )}

                {/* A string stat is drawn from a pool exactly as a string
                    cosmetic is. `stats_schema` declares no `allowed` list, so
                    the values are free-form and none can fall outside one. */}
                {rule && row.type === "string" && (
                  <div className={styles.roll}>
                    <WeightedValues
                      keyName={row.key}
                      list={false}
                      allowed={[]}
                      values={rule.values ?? []}
                      outside={[]}
                      onChange={(values) => patch({ ...rule, values })}
                    />
                  </div>
                )}

                <RowErrors error={error} />
              </div>
            );
          })}
        </div>
      </FieldRow>

      {metaRows.length === 0 ? (
        /* The Meta group is simply gone when the schema declares nothing, and
           a group that disappears without a word reads as a section that
           failed to draw itself. Said out loud, exactly as the help-kinds row
           says it about `help_request.types` — but as info rather than a
           warning: a project with no cosmetics is not misconfigured, and an
           alarm over an ordinary shape teaches an operator to ignore alarms. */
        <div className={styles.alertGap}>
          <Alert variant="info" title="This project declares no cosmetics">
            <code>profile.meta_schema</code> is empty, so there is no cosmetic
            for a bot to wear and the Meta group has no rows to draw — the stats
            above are the whole card. Declare them in{" "}
            <Link to={schemaHref}>Profile → Meta schema</Link> and they appear
            here, one row per key.
          </Alert>
        </div>
      ) : (
        <FieldRow
          name="Meta"
          apiName="bots.profile.meta"
          stacked
          hint={
            <>
              Cosmetics, drawn by weight — a weight is a share, not a
              percentage, so adding a value never asks you to re-balance the
              rest. Values are checked against the key's <code>allowed</code>{" "}
              list at save time, and a bot never receives a cosmetic that is
              only sold for money. A key the schema declares as a number or a
              flag is drawn the way the stat of that type is, from a range or a
              chance — and never grows: a cosmetic that climbs is a counter, and
              a counter belongs above.
            </>
          }
        >
          <div className={styles.editor}>
            {metaRows.map((row) => {
              const at = metaAt.get(row.key);
              const rule = at === undefined ? undefined : value.meta[at];
              const error = at === undefined ? undefined : errors.profileMeta[at];
              const patch = (next: BotProfileMetaRule) => putMeta(row.key, next);
              const pool = drawsFromAPool(row.type);
              return (
                <div key={row.key} className={rowClass(!!rule, error)}>
                  <div className={styles.head}>
                    <Checkbox
                      checked={!!rule}
                      aria-label={`Rule for ${row.key}`}
                      onChange={(on) =>
                        putMeta(
                          row.key,
                          on ? blankRule(row.key, row.type) : null,
                        )
                      }
                    />
                    <span className={styles.key}>{row.key}</span>
                    <Badge>{row.type}</Badge>
                    <span className={styles.spacer} />
                    {!rule ? (
                      <span className={styles.note}>{NOT_FILLED}</span>
                    ) : error && error.outside.length > 0 ? (
                      <Badge variant="warning">
                        {error.outside.length}{" "}
                        {error.outside.length === 1 ? "value" : "values"}{" "}
                        outside the allow-list
                      </Badge>
                    ) : pool ? (
                      <span className={styles.note}>
                        {allowNote(row.allowed, rule.values ?? [])}
                      </span>
                    ) : null}
                  </div>

                  {rule && pool && (
                    <div className={styles.roll}>
                      {row.type === "string[]" && (
                        <>
                          <span className={styles.micro}>how many</span>
                          <Input
                            {...numBox(error, "count")}
                            type="number"
                            value={rule.count_min ?? ""}
                            aria-label={`${row.key} count minimum`}
                            onChange={(e) =>
                              patch(
                                withNumber(rule, "count_min", e.target.value),
                              )
                            }
                          />
                          <span className={styles.dash}>–</span>
                          <Input
                            {...numBox(error, "count")}
                            type="number"
                            value={rule.count_max ?? ""}
                            aria-label={`${row.key} count maximum`}
                            onChange={(e) =>
                              patch(
                                withNumber(rule, "count_max", e.target.value),
                              )
                            }
                          />
                          <span className={styles.vsep} />
                        </>
                      )}
                      <WeightedValues
                        keyName={row.key}
                        list={row.type === "string[]"}
                        allowed={row.allowed}
                        values={rule.values ?? []}
                        outside={error?.outside ?? []}
                        onChange={(values) => patch({ ...rule, values })}
                      />
                    </div>
                  )}

                  {/* The numeric forms are the stats collection's, minus the
                      growth: a cosmetic does not climb, and the number that
                      would is a counter, which belongs in stats. */}
                  {rule && row.type === "int" && (
                    <div className={styles.roll}>
                      <span className={styles.micro}>start</span>
                      <Input
                        {...numBox(error, "start")}
                        type="number"
                        value={rule.start_min ?? ""}
                        aria-label={`${row.key} start minimum`}
                        onChange={(e) =>
                          patch(withNumber(rule, "start_min", e.target.value))
                        }
                      />
                      <span className={styles.dash}>–</span>
                      <Input
                        {...numBox(error, "start")}
                        type="number"
                        value={rule.start_max ?? ""}
                        aria-label={`${row.key} start maximum`}
                        onChange={(e) =>
                          patch(withNumber(rule, "start_max", e.target.value))
                        }
                      />
                    </div>
                  )}

                  {rule && row.type === "bool" && (
                    <div className={styles.roll}>
                      <span className={styles.micro}>true in</span>
                      <Input
                        {...numBox(error, "true_percent")}
                        type="number"
                        value={rule.true_percent ?? ""}
                        aria-label={`${row.key} true percent`}
                        onChange={(e) =>
                          patch(
                            withNumber(rule, "true_percent", e.target.value),
                          )
                        }
                      />
                      <span className={styles.unit}>% of bots</span>
                    </div>
                  )}

                  <RowErrors error={error} />
                </div>
              );
            })}
          </div>
        </FieldRow>
      )}

      <FieldRow
        name="Sample roll"
        apiName="preview"
        stacked
        hint="Three draws from the rules above, made in the browser. Nothing is saved and no bot is touched — it is here because a table of ranges does not tell you what a roster will look like."
      >
        <SampleRoll statRows={statRows} metaRows={metaRows} profile={value} />
      </FieldRow>
    </>
  );
}

/* ---------------------------------------------------------------- the rows */

/**
 * The Stats group: every `int`, `bool` or `string` field the project declared,
 * in its order.
 *
 * A `float` gets no row — the roller has no shape for one — and neither does a
 * `string[]`: the platform's own validation of a player's stats does not know
 * that type, so a bot would be carrying a value the game itself could not
 * write. A row that cannot be filled in is worse than no row.
 */
function buildStatRows(schema: SchemaField[]): StatRow[] {
  const rows: StatRow[] = [];
  const seen = new Set<string>();
  for (const field of schema) {
    if (!STAT_ROW_TYPES.includes(field.type)) continue;
    if (!field.key || seen.has(field.key)) continue;
    seen.add(field.key);
    rows.push({ key: field.key, type: field.type as StatRow["type"] });
  }
  return rows;
}

const STAT_ROW_TYPES: string[] = ["int", "bool", "string"];

/** Five digits is where the mockup's narrow box stops fitting (L308). */
const BIG_NUMBER = 10_000;

/** Does this rule's own numbers need the wider boxes? */
function holdsABigNumber(rule: BotProfileStatRule | undefined): boolean {
  if (!rule) return false;
  return (rule.start_max ?? 0) >= BIG_NUMBER;
}

/**
 * The Meta group: every key of the schema, in its order. All four declarable
 * types have a rule form — a project that declared its prestige level as a
 * meta int would otherwise have no way to give it to a bot at all, and the
 * empty field beside four filled ones is exactly the tell this section removes.
 */
function buildMetaRows(schema: MetaSchemaField[]): MetaRow[] {
  const rows: MetaRow[] = [];
  const seen = new Set<string>();
  for (const field of schema) {
    if (!field.key || seen.has(field.key)) continue;
    seen.add(field.key);
    rows.push({
      key: field.key,
      type: field.type,
      allowed: field.allowed ?? [],
    });
  }
  return rows;
}

/** A number box: the mockup's `.num`, plus whatever the row's error says about
 *  it. Border-only, because the reason is printed once under the row. */
function numBox(
  error: BotProfileRowError | undefined,
  group: BotProfileField,
  wide = false,
) {
  const bad = !!error?.fields.includes(group);
  return {
    className: [styles.num, wide ? styles.wide : "", bad ? styles.err : ""]
      .filter(Boolean)
      .join(" "),
    // Every number in a rule is an integer on the wire, so the spinner steps
    // by one and the browser marks a fraction as invalid. It cannot refuse
    // one — the validation does that — but a box that offers `1.5` is offering
    // a value the server would read as no value at all.
    step: 1,
    "aria-invalid": bad || undefined,
  };
}

function rowClass(filled: boolean, error?: BotProfileRowError): string {
  return [styles.row, filled ? "" : styles.off, error ? styles.invalid : ""]
    .filter(Boolean)
    .join(" ");
}

/** The row says why once, under its boxes — the section's alert counts the
 *  same problems, and the save bar refuses the config over them. */
function RowErrors({ error }: { error?: BotProfileRowError }) {
  if (!error) return null;
  // Two boxes can fail the same way ("must not be negative"); both are painted,
  // and the sentence that explains them is printed once.
  const messages = [...new Set(error.messages)];
  return (
    <>
      {messages.map((message) => (
        <div key={message} className={styles.errText}>
          {message}
        </div>
      ))}
      {error.outside.map((value) => (
        <div key={value} className={styles.errText}>
          <code>{value}</code> {OUTSIDE_ALLOW_LIST}
        </div>
      ))}
    </>
  );
}

/** "3 of 4 allowed values used", or that the key admits anything. */
function allowNote(
  allowed: string[],
  values: BotProfileWeightedValue[],
): string {
  if (allowed.length === 0) return "no allowed list — any value passes";
  const used = new Set(
    values.map((v) => v.value).filter((v) => allowed.includes(v)),
  );
  return `${used.size} of ${allowed.length} allowed values used`;
}

/* ------------------------------------------------------- weighted values */

/**
 * The value pool of one meta key. A chip carries its own weight box, because
 * a weight belongs to the value and a second, parallel list of numbers would
 * be two things obliged to agree.
 */
function WeightedValues({
  keyName,
  list,
  allowed,
  values,
  outside,
  onChange,
}: {
  keyName: string;
  /** A list key draws several values, so «— none —» means nothing to it. */
  list: boolean;
  /** The key's catalogue. `stats_schema` declares none, so a stat's pool is
   *  always free-form. */
  allowed: string[];
  values: BotProfileWeightedValue[];
  outside: string[];
  onChange: (next: BotProfileWeightedValue[]) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  // The weight being typed, held apart from the config: an emptied box is not
  // «weight 0» — see weightOf below.
  const [weight, setWeight] = useState<{ at: number; text: string } | null>(
    null,
  );

  const taken = new Set(values.map((v) => v.value));
  const unused = allowed.filter((v) => !taken.has(v));
  const picker = allowed.length > 0;
  const add = (value: string) => {
    setAdding(false);
    setDraft("");
    if (taken.has(value)) return;
    onChange([...values, { value, weight: NEW_VALUE_WEIGHT }]);
  };

  return (
    // A named group, because the page holds one of these per rule and «+ value»
    // says nothing about which pool it adds to — to a screen reader as much as
    // to a test looking for one of them.
    <div className={styles.chips} role="group" aria-label={`${keyName} values`}>
      {values.map((v, i) => {
        const label = v.value === "" ? "none" : v.value;
        return (
          <span
            key={`${v.value}-${i}`}
            className={[
              styles.chip,
              v.value === "" ? styles.chipNone : "",
              outside.includes(v.value) ? styles.chipOutside : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {v.value === "" ? "— none —" : v.value}
            <input
              className={styles.weight}
              type="number"
              step="1"
              value={weight?.at === i ? weight.text : v.weight}
              aria-label={`${keyName} ${label} weight`}
              onChange={(e) => {
                const raw = e.target.value;
                setWeight({ at: i, text: raw });
                const n = weightOf(raw);
                if (n === undefined) return;
                onChange(
                  values.map((other, j) =>
                    j === i ? { ...other, weight: n } : other,
                  ),
                );
              }}
              // Leaving the box ends the draft, so a box left empty shows the
              // weight the config still holds rather than a blank nobody typed.
              onBlur={() => setWeight(null)}
            />
            <button
              type="button"
              className={styles.chipRemove}
              aria-label={`Remove ${label} from ${keyName}`}
              onClick={() => onChange(values.filter((_, j) => j !== i))}
            >
              ×
            </button>
          </span>
        );
      })}

      {adding && picker && (
        <Select
          className={styles.valueSelect}
          value=""
          options={unused.map((v) => ({
            value: v,
            label: v,
            mono: true,
          }))}
          placeholder="pick a value"
          aria-label={`Add a value to ${keyName}`}
          onChange={add}
        />
      )}
      {adding && !picker && (
        <input
          className={styles.valueInput}
          autoFocus
          value={draft}
          placeholder="value"
          aria-label={`Add a value to ${keyName}`}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              if (draft.trim()) add(draft.trim());
            } else if (e.key === "Escape") {
              setAdding(false);
              setDraft("");
            }
          }}
          onBlur={() => {
            if (draft.trim()) add(draft.trim());
            else setAdding(false);
          }}
        />
      )}
      {!adding && (
        <button
          type="button"
          className={styles.chipAdd}
          disabled={picker && unused.length === 0}
          onClick={() => setAdding(true)}
        >
          + value
        </button>
      )}
      {/* "— none —" is a value like any other, weight and all: it is how a key
          is left off SOME bots rather than all of them. It means nothing for an
          array, where the empty draw is `[]`. */}
      {!list && !taken.has("") && (
        <button
          type="button"
          className={styles.chipAdd}
          onClick={() => add("")}
        >
          + none
        </button>
      )}
    </div>
  );
}

/* ----------------------------------------------------------- sample roll */

interface Draw {
  key: string;
  text: string;
}

/**
 * Three draws from the rules, made here in the browser. Nothing is saved and
 * no bot is touched: a table of ranges does not tell an operator what a roster
 * will look like, and this is the cheapest way to show them.
 */
function SampleRoll({
  statRows,
  metaRows,
  profile,
}: {
  statRows: StatRow[];
  metaRows: MetaRow[];
  profile: BotsProfileConfig;
}) {
  const [nonce, setNonce] = useState(0);
  // Which keys are ticked — the sample's SHAPE, and the only part of the rules
  // it redraws itself for. Depending on `profile` whole would re-roll all three
  // draws on every keystroke: typing a range would leave numbers flickering
  // under the operator's hands, and the Re-roll button would have nothing left
  // to do. Numbers typed since the last draw are picked up by that button.
  const shape = [
    // Whether there is an avatar rule at all — the same question a ticked key
    // answers for the rows below, and the only part of the pool that changes
    // the sample's shape rather than its numbers.
    (profile.avatar ?? []).length > 0 ? "avatar" : "",
    statRows.map((r) => r.key).join(","),
    metaRows.map((r) => r.key).join(","),
    profile.stats.map((r) => r.key).join(","),
    profile.meta.map((r) => r.key).join(","),
  ].join("|");
  const samples = useMemo(
    () => [0, 1, 2].map(() => rollProfile(statRows, metaRows, profile)),
    // `nonce` and `shape` are not read: bumping the first is the Re-roll
    // button, and the second changes when a key is ticked or unticked.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shape, nonce],
  );

  return (
    <>
      <div className={styles.sampleHead}>
        <span className={styles.micro}>as the roster would show them</span>
        <Button
          size="sm"
          variant="ghost"
          icon={<RotateCw size={13} aria-hidden />}
          onClick={() => setNonce((n) => n + 1)}
        >
          Re-roll
        </Button>
      </div>
      <div className={styles.samples} role="group" aria-label="Sample roll">
        {samples.map((draws, i) => (
          <div key={i} className={styles.sample}>
            {draws.length === 0 ? (
              <span className={styles.sampleEmpty}>
                No rule is filled in — a bot is created with a name and a
                country, and nothing else. An avatar is one of the things a rule
                has to ask for: without one above, the bot is created without a
                picture.
              </span>
            ) : (
              draws.map((draw, j) => (
                <Fragment key={draw.key}>
                  {j > 0 && "\n"}
                  <span className={styles.sampleKey}>{draw.key}</span>
                  {pad(draw.key) + draw.text}
                </Fragment>
              ))
            )}
          </div>
        ))}
      </div>
    </>
  );
}

function rollProfile(
  statRows: StatRow[],
  metaRows: MetaRow[],
  profile: BotsProfileConfig,
): Draw[] {
  const out: Draw[] = [];
  // The avatar leads, in the order the game reads the card. No rule is no
  // line: an absent pool is not a bot whose avatar came up empty, it is a bot
  // the platform was never asked to dress.
  const avatar = profile.avatar ?? [];
  if (avatar.length > 0) {
    const drawn = pickWeighted(avatar);
    // The empty draw is the «— none —» share of the pool, spelled the way the
    // chip that put it there spells it.
    out.push({ key: "avatar", text: drawn ? drawn : NONE_DRAWN });
  }
  for (const row of statRows) {
    const rule = profile.stats.find((r) => r.key === row.key);
    if (!rule) continue;
    if (row.type === "bool") {
      out.push({
        key: row.key,
        text: Math.random() * 100 < (rule.true_percent ?? 0) ? "true" : "false",
      });
      continue;
    }
    if (row.type === "string") {
      const drawn = pickWeighted(rule.values ?? []);
      out.push({ key: row.key, text: drawn ? drawn : NONE_DRAWN });
      continue;
    }
    const min = rule.start_min ?? 0;
    out.push({
      key: row.key,
      text: formatCount(randomInt(min, Math.max(min, rule.start_max ?? min))),
    });
  }
  for (const row of metaRows) {
    const rule = profile.meta.find((r) => r.key === row.key);
    if (!rule) continue;
    // The numeric forms are drawn exactly as a stat of the same type is.
    if (row.type === "bool") {
      out.push({
        key: row.key,
        text: Math.random() * 100 < (rule.true_percent ?? 0) ? "true" : "false",
      });
      continue;
    }
    if (row.type === "int") {
      const from = rule.start_min ?? 0;
      out.push({
        key: row.key,
        text: formatCount(
          randomInt(from, Math.max(from, rule.start_max ?? from)),
        ),
      });
      continue;
    }
    if (row.type === "string") {
      const drawn = pickWeighted(rule.values ?? []);
      // An empty draw is a bot that does not carry the key at all.
      out.push({ key: row.key, text: drawn ? drawn : NONE_DRAWN });
      continue;
    }
    // An array is drawn value by value, without repeats: a bot wearing the same
    // badge twice would give the whole trick away.
    const pool = (rule.values ?? []).filter(
      (v) => v.value !== "" && v.weight > 0,
    );
    const min = Math.max(0, rule.count_min ?? 0);
    const want = randomInt(min, Math.max(min, rule.count_max ?? min));
    const drawn: string[] = [];
    while (drawn.length < want && pool.length > 0) {
      const value = pickWeighted(pool);
      if (value === undefined) break;
      drawn.push(value);
      pool.splice(
        pool.findIndex((v) => v.value === value),
        1,
      );
    }
    out.push({ key: row.key, text: JSON.stringify(drawn) });
  }
  return out;
}

function pickWeighted(values: BotProfileWeightedValue[]): string | undefined {
  const pool = values.filter((v) => v.weight > 0);
  const total = pool.reduce((n, v) => n + v.weight, 0);
  if (total <= 0) return undefined;
  let point = Math.random() * total;
  for (const v of pool) {
    point -= v.weight;
    if (point < 0) return v.value;
  }
  return pool[pool.length - 1].value;
}

function randomInt(min: number, max: number): number {
  const lo = Math.ceil(min);
  const hi = Math.floor(max);
  return lo + Math.floor(Math.random() * (hi - lo + 1));
}

function pad(key: string): string {
  return " ".repeat(Math.max(1, SAMPLE_KEY_WIDTH - key.length));
}

/* ------------------------------------------------------- writing the rules */

function indexByKey(rules: { key: string }[]): Map<string, number> {
  const out = new Map<string, number>();
  rules.forEach((rule, i) => {
    if (!out.has(rule.key)) out.set(rule.key, i);
  });
  return out;
}

/**
 * Write one key's rule, keeping the array in the order of the editor's rows.
 *
 * A rule for a key this editor draws no row for — one the schema no longer
 * declares — is never moved and never dropped: the operator can still see it
 * in the JSON view, and deleting config because a list on screen is shorter
 * than the file is not this panel's decision to make.
 *
 * Unticking takes EVERY rule for the key, not the first one. A key listed
 * twice is only reachable through the JSON view, and it is exactly the session
 * where a checkbox that stays ticked after being cleared — because a second
 * rule for the same key survived — reads as a broken editor rather than as
 * config the operator wrote.
 */
function writeRule<T extends { key: string }>(
  list: T[],
  order: string[],
  key: string,
  rule: T | null,
): T[] {
  const at = list.findIndex((r) => r.key === key);
  if (!rule) return at < 0 ? list : list.filter((r) => r.key !== key);
  if (at >= 0) return list.map((r, i) => (i === at ? rule : r));
  const rank = order.indexOf(key);
  const before = list.findIndex((r) => {
    const other = order.indexOf(r.key);
    return other >= 0 && other > rank;
  });
  return before < 0
    ? [...list, rule]
    : [...list.slice(0, before), rule, ...list.slice(before)];
}

/**
 * Write one number box. An emptied box DELETES its key rather than storing 0:
 * "not set" is the absence of the key, and a stored zero is a rule that rolls
 * zeros.
 */
function withNumber<T extends object, K extends keyof T>(
  rule: T,
  field: K,
  raw: string,
): T {
  const next = { ...rule };
  const n = Number(raw);
  if (raw.trim() === "" || !Number.isFinite(n)) delete next[field];
  else next[field] = n as T[K];
  return next;
}

/**
 * What ticking a key writes. A pool starts empty and says so; a range and a
 * chance start as nothing at all, and the row says what it still needs.
 */
function blankRule(key: string, type: StatRow["type"] | MetaRow["type"]) {
  return type === "string" || type === "string[]"
    ? { key, values: [] as BotProfileWeightedValue[] }
    : { key };
}

/**
 * The number in a weight box, or nothing at all while it is being retyped.
 *
 * An empty box used to be written back as 0, which is a real setting — «this
 * value never comes up» — and React then put that 0 straight back into the box
 * the operator was still holding Backspace in, so a weight could not be
 * retyped at all. Nothing is written until the text is a number again; the box
 * itself keeps what was typed, exactly as the custom-names textarea keeps a
 * half-written line.
 */
function weightOf(raw: string): number | undefined {
  if (raw.trim() === "") return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}
